import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { Card, CardNavigation } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard } from "../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, TextArea } from "../../components/form";
import { Icon } from "../../components/icon";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent, PlaygroundDefault } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { MutedText, Paragraph } from "../../components/text";
import { displayFormControl } from "../../ui/alert";
import { formatFloat, formatNumber } from "../../ui/format";
import { valueFrom } from "../../util";

const clusterColors = [
    "#2d88d3",
    "#d66a2d",
    "#2aa66f",
    "#9a60d1",
    "#d04f73",
    "#7e9f2f",
    "#1f9da6",
    "#b78c2e"
];

export default function (ctx) {

    const collectionId = ctx.params.id;
    const result = el.state.create(null);
    const loading = el.state.create(false);
    const error = el.state.create(null);
    const selectedNode = el.state.create(null);
    const graphPan = { x: 0, y: 0, zoom: 1 };

    function buildPayload() {
        return {
            term: valueFrom("navigationTerm"),
            maxHops: valueFrom("navigationMaxHops"),
            seedCount: valueFrom("navigationSeedCount"),
            neighborsPerNode: valueFrom("navigationNeighborsPerNode"),
            minScore: valueFrom("navigationMinScore")
        };
    }

    async function runNavigation() {
        loading.value = true;
        error.value = null;
        selectedNode.value = null;

        try {
            const response = await api.postJson(`/api/v1/collections/${collectionId}/navigation`, buildPayload());
            result.value = response.data;
            selectedNode.value = response.data?.nodes?.find(x => x.seed) ?? response.data?.nodes?.[0] ?? null;
        }
        catch (ex) {
            error.value = ex;
        }
        finally {
            loading.value = false;
        }
    }

    return Dashboard(
        {
            classList: ["full-size"]
        },
        Card(
            CardNavigation(
                CommandBar(
                    Button(
                        Icon("arrow-left-line"),
                        "Back to documents",
                        {
                            onClick() {
                                page(`/dashboard/collections/${collectionId}/documents`);
                            }
                        }
                    ),
                    Button(
                        Icon("terminal-line"),
                        "Playground",
                        {
                            onClick() {
                                page(`/dashboard/collections/${collectionId}/playground`);
                            }
                        }
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            PlaygroundAsideContainer(
                                SizableContainer(
                                    SizableSection("Search",
                                        el(".vector-navigation-actions",
                                            TextArea(null, "semantic retrieval", {
                                                id: "navigationTerm",
                                                placeholder: "Search term..."
                                            }),
                                            PrimaryButton(Icon("share-forward-line"), "Run navigation", {
                                                onClick() {
                                                    runNavigation();
                                                }
                                            })
                                        )
                                    ),
                                    SizableSection("Parameters",
                                        numberEditor("navigationMinScore", "Minimum score", 0.4, 0.01, 0.99, 0.05, formatFloat),
                                        numberEditor("navigationSeedCount", "Seeds", 5, 1, 20, 1, formatNumber),
                                        numberEditor("navigationNeighborsPerNode", "Neighbors", 4, 1, 8, 1, formatNumber),
                                        numberEditor("navigationMaxHops", "Max hops", 5, 1, 5, 1, formatNumber)
                                    )
                                )
                            )
                        )
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        el.state.listening([result, loading, error, selectedNode], () =>
                            PlaygroundContent(
                                el(".vector-navigation-page",
                                    renderNavigationContent(result.value, loading.value, error.value, selectedNode, collectionId, graphPan)
                                )
                            )
                        )
                    )
                )
            )
        )
    )
}

function numberEditor(id, title, value, min, max, step, format) {
    return Editor(
        {
            id,
            value,
            renderValue(currentValue) {
                return Button(format(currentValue));
            },
            manipulateValue(currentValue, callback) {
                displayFormControl({
                    title: this.editorTitle,
                    message: this.editorParagraphs,
                    formControlItem: NumberInput({ value: currentValue, min, max, step }),
                    onConfirm: callback,
                    validate: []
                })
            }
        },
        EditorDescription(
            EditorTitle(title)
        )
    );
}

function renderNavigationContent(result, loading, error, selectedNode, collectionId, graphPan) {
    if (loading) {
        return PlaygroundDefault("loader-4-line", "Loading navigation...");
    }

    if (error) {
        return PlaygroundDefault("error-warning-line", "Navigation failed.");
    }

    if (!result) {
        return PlaygroundDefault("share-forward-line", "Run navigation.");
    }

    if (result.status === "building") {
        return PlaygroundDefault("loader-4-line", "Cluster building.");
    }

    if (result.status === "empty") {
        return PlaygroundDefault("inbox-line", "No documents found.");
    }

    if (result.status === "too_large") {
        return PlaygroundDefault("stack-line", "Collection too large.");
    }

    if (!result.nodes?.length) {
        return PlaygroundDefault("inbox-line", "No matches found.");
    }

    return el(".vector-navigation-shell",
        NavigationLegend(),
        GraphCanvas(result, selectedNode, graphPan),
        NavigationInspector(result, selectedNode, collectionId)
    );
}

function NavigationLegend() {
    return el(".vector-navigation-legend",
        el("span.vector-navigation-legend-item",
            el("span.vector-navigation-legend-dot.vector-navigation-legend-start"),
            "Ponto inicial"
        ),
        el("span.vector-navigation-legend-item",
            el("span.vector-navigation-legend-line"),
            "Caminho selecionado"
        )
    );
}

function GraphCanvas(result, selectedNode, graphPan) {
    return el("canvas.vector-navigation-canvas[redrawable]",
        {
            mount() {
                this.redraw();
            },
            onContextMenu(event) {
                event.preventDefault();
            },
            onWheel(event) {
                const layout = this._layout;
                if (!layout) {
                    return;
                }

                event.preventDefault();
                const rect = this.getBoundingClientRect();
                const cursorX = event.clientX - rect.left;
                const cursorY = event.clientY - rect.top;
                const worldX = (cursorX - graphPan.x) / graphPan.zoom;
                const worldY = (cursorY - graphPan.y) / graphPan.zoom;
                const factor = event.deltaY < 0 ? 1.12 : 0.88;

                graphPan.zoom = Math.min(3.5, Math.max(0.35, graphPan.zoom * factor));
                graphPan.x = cursorX - worldX * graphPan.zoom;
                graphPan.y = cursorY - worldY * graphPan.zoom;
                drawGraph(this, layout, selectedNode.value?.id, graphPan);
            },
            onMouseDown(event) {
                if (event.button !== 2) {
                    return;
                }

                event.preventDefault();
                this._panning = {
                    startX: event.clientX,
                    startY: event.clientY,
                    originX: graphPan.x,
                    originY: graphPan.y
                };
                this.classList.add("panning");
            },
            onMouseMove(event) {
                if (!this._panning) {
                    return;
                }

                event.preventDefault();
                graphPan.x = this._panning.originX + event.clientX - this._panning.startX;
                graphPan.y = this._panning.originY + event.clientY - this._panning.startY;
                drawGraph(this, this._layout, selectedNode.value?.id, graphPan);
            },
            onMouseUp(event) {
                if (!this._panning) {
                    return;
                }

                event.preventDefault();
                this._panning = null;
                this.classList.remove("panning");
            },
            onMouseLeave() {
                if (!this._panning) {
                    return;
                }

                this._panning = null;
                this.classList.remove("panning");
            },
            onClick(event) {
                if (event.button !== 0) {
                    return;
                }

                const layout = this._layout;
                if (!layout) {
                    return;
                }

                const rect = this.getBoundingClientRect();
                const x = (event.clientX - rect.left - graphPan.x) / graphPan.zoom;
                const y = (event.clientY - rect.top - graphPan.y) / graphPan.zoom;
                let best = null;
                let bestDistance = Number.MAX_VALUE;

                for (const node of layout.nodes) {
                    const dx = node.x - x;
                    const dy = node.y - y;
                    const distance = dx * dx + dy * dy;
                    const radius = node.radius + 8;
                    if (distance <= radius * radius && distance < bestDistance) {
                        best = node;
                        bestDistance = distance;
                    }
                }

                if (best) {
                    selectedNode.value = best.data;
                }
            },
            $functions: {
                redraw() {
                    this._layout = buildGraphLayout(this, result);
                    drawGraph(this, this._layout, selectedNode.value?.id, graphPan);
                }
            }
        }
    );
}

function NavigationInspector(result, selectedNode, collectionId) {
    const node = selectedNode.value;

    if (!node) {
        return el("aside.vector-navigation-inspector.vector-navigation-inspector-empty",
            MutedText("Select a node")
        );
    }

    const nodesById = new Map(result.nodes.map(currentNode => [currentNode.id, currentNode]));
    const timeline = [node];
    let current = node;

    while (current && !current.seed && current.hop > 0) {
        const previousEdge = result.edges.find(edge => {
            const from = nodesById.get(edge.from);
            return edge.to === current.id && from?.hop === current.hop - 1;
        });

        if (!previousEdge) {
            break;
        }

        current = nodesById.get(previousEdge.from);

        if (current) {
            timeline.unshift(current);
        }
    }

    const isIsolatedStart = node.seed && !result.edges.some(edge => edge.from === node.id || edge.to === node.id);

    return el("aside.vector-navigation-inspector",
        el("div.vector-navigation-inspector-heading",
            el("strong", node.name),
            node.seed && el("span.vector-navigation-seed", "Ponto inicial")
        ),
        el("div.vector-navigation-route-summary",
            isIsolatedStart
                ? "Este documento veio da busca inicial, mas não abriu caminho para outros documentos."
                : node.seed ? "Este documento iniciou a navegação." : `Encontrado a ${formatNumber(node.hop)} salto${node.hop === 1 ? "" : "s"} do ponto inicial.`
        ),
        el(".vector-navigation-metrics",
            metricItem("Similaridade", formatFloat(node.score)),
            metricItem("Distância", node.seed ? "Ponto inicial" : `${formatNumber(node.hop)} salto${node.hop === 1 ? "" : "s"}`),
            metricItem("Cluster", node.clusterId),
            metricItem("Outlier", node.outlierScore == null ? "-" : formatFloat(node.outlierScore))
        ),
        el(".vector-navigation-timeline",
            el("div.vector-navigation-section-title", "Caminho"),
            timeline.length === 1 && el("div.vector-navigation-timeline-note", "Sem saltos conectados para este ponto inicial."),
            el("ol",
                ...timeline.map((timelineNode, index) =>
                    el("li",
                        {
                            classList: [
                                timelineNode.id === selectedNode.value?.id && "selected",
                                timelineNode.seed && "seed"
                            ]
                        },
                        Button(
                            el("span.vector-navigation-timeline-index", timelineNode.seed ? "0" : formatNumber(timelineNode.hop)),
                            el("span.vector-navigation-timeline-body",
                                el("span.vector-navigation-timeline-title-row",
                                    el("strong", timelineNode.name),
                                    el("span.vector-navigation-timeline-distance", index === 0 ? "Ponto inicial" : `${formatNumber(timelineNode.hop)} salto${timelineNode.hop === 1 ? "" : "s"}`)
                                ),
                                el("span.vector-navigation-timeline-preview", timelineNode.preview)
                            ),
                            {
                                onClick() {
                                    selectedNode.value = timelineNode;
                                }
                            }
                        )
                    )
                )
            )
        ),
        el(".vector-navigation-preview", Paragraph(node.preview)),
        PrimaryButton(Icon("file-text-line"), "Open document", {
            onClick() {
                page(`/dashboard/collections/${collectionId}/documents/${node.id}`);
            }
        })
    );
}

function metricItem(label, value) {
    return el(".vector-navigation-metric",
        el("span", label),
        el("strong", value)
    );
}

function buildGraphLayout(canvas, result) {
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(rect.width, 320);
    const height = Math.max(rect.height, 260);
    const centerX = width / 2;
    const centerY = height / 2;
    const nodes = result.nodes.map((node, index) => {
        const angle = index * 2.399963229728653;
        const radius = 34 + node.hop * 84 + Math.abs(node.clusterId % 3) * 18;
        return {
            data: node,
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius,
            vx: 0,
            vy: 0,
            radius: node.seed ? 10 : 7
        };
    });
    const nodesById = new Map(nodes.map(node => [node.data.id, node]));
    const edges = result.edges
        .map(edge => ({
            ...edge,
            fromNode: nodesById.get(edge.from),
            toNode: nodesById.get(edge.to)
        }))
        .filter(edge => edge.fromNode && edge.toNode);

    for (let iteration = 0; iteration < 150; iteration++) {
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const a = nodes[i];
                const b = nodes[j];
                const dx = b.x - a.x || 0.01;
                const dy = b.y - a.y || 0.01;
                const distanceSquared = dx * dx + dy * dy;
                const force = Math.min(2.2, 850 / distanceSquared);
                const distance = Math.sqrt(distanceSquared);
                const fx = dx / distance * force;
                const fy = dy / distance * force;
                a.vx -= fx;
                a.vy -= fy;
                b.vx += fx;
                b.vy += fy;
            }
        }

        for (const edge of edges) {
            const a = edge.fromNode;
            const b = edge.toNode;
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const distance = Math.max(Math.sqrt(dx * dx + dy * dy), 0.01);
            const target = 82 + edge.hop * 10;
            const force = (distance - target) * (0.01 + edge.score * 0.01);
            const fx = dx / distance * force;
            const fy = dy / distance * force;
            a.vx += fx;
            a.vy += fy;
            b.vx -= fx;
            b.vy -= fy;
        }

        for (const node of nodes) {
            const ring = 38 + node.data.hop * 92;
            const dx = node.x - centerX;
            const dy = node.y - centerY;
            const distance = Math.max(Math.sqrt(dx * dx + dy * dy), 0.01);
            const ringForce = (distance - ring) * 0.004;
            node.vx -= dx / distance * ringForce;
            node.vy -= dy / distance * ringForce;
            node.vx *= 0.84;
            node.vy *= 0.84;
            node.x = Math.min(width - 26, Math.max(26, node.x + node.vx));
            node.y = Math.min(height - 26, Math.max(26, node.y + node.vy));
        }
    }

    return { nodes, edges, width, height, nodesById };
}

function drawGraph(canvas, layout, selectedId, graphPan) {
    if (!layout) {
        return;
    }

    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(layout.width * ratio);
    canvas.height = Math.floor(layout.height * ratio);
    canvas.style.width = layout.width + "px";
    canvas.style.height = layout.height + "px";

    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, layout.width, layout.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const selectedPath = getSelectedPath(layout, selectedId);
    ctx.save();
    ctx.translate(graphPan.x, graphPan.y);
    ctx.scale(graphPan.zoom, graphPan.zoom);

    for (const edge of layout.edges) {
        const highlighted = selectedPath.edgeKeys.has(edgeKey(edge.from, edge.to));
        ctx.beginPath();
        ctx.globalAlpha = highlighted ? 0.96 : 0.26;
        ctx.strokeStyle = highlighted ? "#1f6feb" : colorForCluster(edge.fromNode.data.clusterId);
        ctx.lineWidth = highlighted ? 4.5 : 1.4 + edge.score * 2.1;
        ctx.moveTo(edge.fromNode.x, edge.fromNode.y);
        ctx.lineTo(edge.toNode.x, edge.toNode.y);
        ctx.stroke();
    }

    ctx.globalAlpha = 1;

    for (const node of layout.nodes) {
        const selected = node.data.id === selectedId;
        const inPath = selectedPath.nodeIds.has(node.data.id);
        const isStart = node.data.seed;

        if (isStart || inPath || selected) {
            ctx.beginPath();
            ctx.fillStyle = isStart ? "rgba(31,111,235,.12)" : "rgba(31,111,235,.08)";
            ctx.strokeStyle = isStart ? "#1f6feb" : "rgba(31,111,235,.72)";
            ctx.lineWidth = isStart ? 3 : 2;
            ctx.arc(node.x, node.y, node.radius + (selected ? 9 : isStart ? 7 : 5), 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
        }

        ctx.beginPath();
        ctx.fillStyle = isStart ? "#1f6feb" : colorForCluster(node.data.clusterId);
        ctx.strokeStyle = selected ? "#0f172a" : "rgba(255,255,255,.82)";
        ctx.lineWidth = selected ? 3 : 1.4;
        ctx.arc(node.x, node.y, selected ? node.radius + 4 : node.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

    }

    ctx.restore();
}

function getSelectedPath(layout, selectedId) {
    const nodeIds = new Set();
    const edgeKeys = new Set();

    if (!selectedId || !layout.nodesById.has(selectedId)) {
        return { nodeIds, edgeKeys };
    }

    let current = layout.nodesById.get(selectedId);
    nodeIds.add(current.data.id);

    while (current && !current.data.seed && current.data.hop > 0) {
        const previousEdge = layout.edges.find(edge =>
            edge.to === current.data.id &&
            edge.fromNode.data.hop === current.data.hop - 1);

        if (!previousEdge) {
            break;
        }

        edgeKeys.add(edgeKey(previousEdge.from, previousEdge.to));
        current = previousEdge.fromNode;
        nodeIds.add(current.data.id);
    }

    return { nodeIds, edgeKeys };
}

function edgeKey(from, to) {
    return `${from}:${to}`;
}

function colorForCluster(clusterId) {
    if (clusterId < 0) {
        return "#8a8a8a";
    }

    return clusterColors[clusterId % clusterColors.length];
}
